import { FunctionComponent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapTemplate } from '@templates';
import {
  AssetTypeConfig,
  emptyServiceLocationDetails,
  ErrorMessages,
  PublicLightingElectricVehicleChargers,
} from '@types';
import {
  addressResponseType,
  convertAddressToServiceLocation,
  convertServiceLocationToAddress,
  errorFormatter,
  issueLocationValidation,
  stringFieldSchema,
} from '@utils';
import { ZodError } from 'zod';
import { LatLngTuple } from 'leaflet';
import { SelectedLocationTextContent } from './SelectedLocationTextContent';
import { useEVChargerStore } from '../../../../hooks/useEVChargerStore';
import { RoutesList } from '../../../routes/Routes';

export const IssueLocation: FunctionComponent = () => {
  const navigate = useNavigate();
  const [errorMessages, setErrorMessages] = useState<ErrorMessages[]>([]);
  const errorPath = `issue-Location`;
  const pageTitle = 'Where is the EV charging point located?';

  const {
    update,
    data: { serviceLocationDetails, provider, assetDetails },
  } = useEVChargerStore();

  const subText =
    provider === 'ShellUbitricity'
      ? 'Select an EV charging point location on the map. Each location is marked by a blue circle.'
      : 'Click on the map to select a location.';

  const { latitude, longitude } = serviceLocationDetails;

  const defaultLocation =
    latitude && longitude ? ([latitude, longitude] as LatLngTuple) : undefined;

  const handleOnPreviousClick = () => {
    navigate(RoutesList.EV_CHARGER_UPLOAD_PHOTO);
  };

  const handleOnNextClick = () => {
    setErrorMessages([]);

    const featureTypeCode =
      (assetDetails as PublicLightingElectricVehicleChargers)
        ?.feature_type_code ?? '';
    try {
      let assetID =
        featureTypeCode === 'PLEV'
          ? ((assetDetails as PublicLightingElectricVehicleChargers)
              ?.central_asset_id ?? '')
          : '';

      if (provider === 'ShellUbitricity') {
        stringFieldSchema('assetID', 'Select a blue circle on the map').parse({
          assetID,
        });
      }

      issueLocationValidation.parse({
        locationIssue: serviceLocationDetails.UPRN ?? '',
      });

      navigate(RoutesList.ISSUE);
    } catch (error) {
      const { errorMessages } = errorFormatter(error as ZodError);
      setErrorMessages([
        { path: errorPath, message: errorMessages[0].message },
      ]);
    }
  };

  const buttonPrevious = {
    label: 'Previous',
    onClick: handleOnPreviousClick,
  };

  const buttonNext = {
    label: 'Next',
    onClick: handleOnNextClick,
  };

  const handleAddressChange = async (address: addressResponseType) => {
    setErrorMessages([]);
    if (Object.keys(address).length === 0) {
      // Clears address and asset details when user clears address from search bar
      update('serviceLocationDetails', emptyServiceLocationDetails);
      update('assetDetails', null);
      return;
    } else {
      update(
        'serviceLocationDetails',
        convertAddressToServiceLocation(address),
      );

      address.asset &&
      (address.asset as PublicLightingElectricVehicleChargers)
        .feature_type_code === 'PLEV'
        ? update(
            'assetDetails',
            address.asset as PublicLightingElectricVehicleChargers,
          )
        : update('assetDetails', null);
    }
  };

  const electricVehicleChargersAsset: AssetTypeConfig = {
    asset: 'PublicLightingElectricVehicleChargers',
    icon: {
      shape: 'circle',
      size: 56,
    },
    // color: '#327A1A',
    onAdd(geojson, marker) {
      const markerElement = marker.getElement();
      const description = `EV charging point selected on ${geojson.properties.site_name}`;
      if (markerElement) {
        markerElement.setAttribute('alt', description);
      }
    },
  };

  const assetsToDisplay =
    provider === 'ShellUbitricity' ? [electricVehicleChargersAsset] : undefined;

  return (
    <MapTemplate
      id={errorPath}
      labelText={pageTitle}
      assetType={assetsToDisplay}
      displayMapAssets={true}
      subText={subText}
      errorPath={errorPath}
      errorMessages={errorMessages}
      defaultLocation={defaultLocation}
      onChangeAddress={handleAddressChange}
      selectedAddress={convertServiceLocationToAddress(serviceLocationDetails)}
      customAddressContent={<SelectedLocationTextContent />}
      buttonNext={buttonNext}
      buttonPrevious={buttonPrevious}
    />
  );
};
